import React, { ReactElement, useState } from "react";
import { IToast } from "../../interfaces/Toast";

interface IToastContextType {
  toasts: IToast[];
  setToasts: (toasts: IToast[]) => void;
}

interface IToastContextProviderProps {
  children: ReactElement;
}

/**
 * The default is a no-op, so a component rendered outside the provider drops a
 * toast on the floor instead of failing. That silence is worth a word in
 * development: a dialog that never rendered because its provider was missing is
 * indistinguishable from one that was never asked to render, and it cost a day
 * of looking in the wrong place when the Switch Network dialog went missing.
 *
 * A warning, not a throw. Throwing would break tests that render components in
 * isolation, and turn a latent problem into an outage. Production behaviour is
 * unchanged.
 */
const missingProvider = (what: string) => () => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error(
      `[ToastContext] ${what} was called with no ToastContextProvider above it. ` +
        `The toast will not appear. Mount ToastContextProvider (see App.tsx).`
    );
  }
};

export const ToastContext = React.createContext<IToastContextType>({
  toasts: [],
  setToasts: missingProvider("setToasts"),
});

export function ToastContextProvider({ children }: IToastContextProviderProps) {
  const [toasts, setToasts] = useState<IToast[]>([]);

  return <ToastContext.Provider value={{ toasts, setToasts }}>{children}</ToastContext.Provider>;
}
